import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Check, User, Search, Heart, PoundSterling, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEP_META = [
  { icon: User, label: 'You' },
  { icon: Search, label: 'Found Us' },
  { icon: Heart, label: 'Decision' },
  { icon: PoundSterling, label: 'Pricing' },
  { icon: MessageSquare, label: 'Thoughts' },
];

// --- Question definitions ---
const FOUND_US_QUESTIONS = [
  {
    id: 'how_heard',
    question: 'How did you first hear about us?',
    subtitle: 'Select all that apply',
    type: 'multi',
    options: ['Google search', 'Instagram', 'Facebook', 'TikTok', 'YouTube', 'Wedding fair', 'Venue recommendation', 'Friend or family recommendation', 'Photographer recommendation', 'Hitched / wedding directory', 'Blog or article', 'Other'],
  },
  {
    id: 'which_wedding_fair',
    question: 'Which wedding fair did you see us at?',
    type: 'text',
    showIf: 'wedding_fair',
  },
  {
    id: 'who_recommended',
    question: 'Who recommended us?',
    subtitle: 'Venue, friend, photographer etc.',
    type: 'text',
    optional: true,
  },
  {
    id: 'first_action',
    question: 'When you first found us, what did you do next?',
    subtitle: 'Select all that apply',
    type: 'multi',
    options: ['Watched our showreel', 'Visited our website', 'Looked at our Instagram', 'Watched videos on YouTube', 'Read reviews', 'Messaged us straight away', 'Other'],
  },
  {
    id: 'research_time',
    question: 'How long did you research us before getting in touch?',
    type: 'single',
    options: ['Same day', 'A few days', 'A week or two', 'Over a month'],
  },
];

// --- BOOKED path questions ---
const DECISION_BOOKED = [
  {
    id: 'how_many_enquired',
    question: 'How many suppliers did you enquire with before booking?',
    type: 'single',
    options: ['Just you', '2 to 3', '4 to 6', 'More than 6'],
  },
  {
    id: 'second_choice',
    question: 'Who was your closest second choice?',
    type: 'text',
    optional: true,
  },
  {
    id: 'ruled_out_reason',
    question: 'What made you rule out your second choice?',
    subtitle: 'Select all that apply',
    type: 'multi',
    options: ['Price', 'Availability', 'Style', 'Slower to respond', 'Gut feeling', 'Less professional', 'Worse reviews', 'Other'],
    optional: true,
  },
  {
    id: 'post_call_research',
    question: 'After your consultation call with us, did you continue looking at other suppliers?',
    type: 'single',
    options: ['No — I was sold after the call', 'Briefly, but came back to you', 'Yes — I compared a few more', 'I didn\'t have a call'],
  },
  {
    id: 'biggest_reason',
    question: 'What was the single biggest reason you chose us?',
    type: 'text',
  },
  {
    id: 'guestbook_influence',
    question: 'Was the Video Messages Guestbook offer a big reason for booking?',
    type: 'single',
    options: ['Yes — it was a huge bonus', 'It helped but wasn\'t the main reason', 'It was nice but didn\'t influence my decision', 'I didn\'t know about it'],
  },
  {
    id: 'decision_factors',
    question: 'Which of these played a part in your decision?',
    subtitle: 'Select all that apply',
    type: 'multi',
    options: ['Quality of work', 'Personality and communication', 'Price', 'Speed of response', 'Consultation call', 'Style and editing', 'Reviews and testimonials', 'Packages felt right', 'Trusted recommendation', 'Professional website', 'Video Messages Guestbook'],
  },
  {
    id: 'received_emails',
    question: 'Do you remember getting emails from us during your decision process?',
    type: 'single',
    options: ['Yes', 'No', 'Not sure'],
  },
  {
    id: 'emails_feeling',
    question: 'How did the emails feel?',
    subtitle: 'Select all that apply',
    type: 'multi',
    options: ['Informative', 'Useful', 'Helpful in deciding', 'Too frequent', 'Annoying', 'Didn\'t read them'],
    showIf: 'received_emails_yes',
  },
  {
    id: 'emails_helped_decide',
    question: 'Did the emails help you decide we were the right choice?',
    type: 'single',
    options: ['Yes — they made a real difference', 'A little', 'Not really', 'I\'d already decided before the emails'],
    showIf: 'received_emails_yes',
  },
  {
    id: 'gut_reaction',
    question: 'When you watched our work, what was your gut reaction?',
    type: 'text',
  },
  {
    id: 'almost_put_off',
    question: 'Did anything almost put you off booking us?',
    type: 'text',
    optional: true,
  },
];

// --- DIDN'T BOOK path questions ---
const DECISION_NOT_BOOKED = [
  {
    id: 'who_booked_instead',
    question: 'Who did you end up booking?',
    type: 'text',
    optional: true,
  },
  {
    id: 'why_not_us',
    question: 'What was the main reason you didn\'t book us?',
    subtitle: 'Select all that apply',
    type: 'multi',
    options: ['Price', 'Availability', 'Style wasn\'t right', 'Slow response', 'Better connection with someone else', 'Package didn\'t fit', 'Didn\'t need video/photo', 'Other'],
  },
  {
    id: 'why_not_detail',
    question: 'Can you tell us a bit more about what swayed your decision?',
    type: 'text',
    optional: true,
  },
  {
    id: 'post_call_feeling',
    question: 'If you had a consultation call, how did it go?',
    type: 'single',
    options: ['Great — but something else won out', 'It was okay', 'It didn\'t sell me', 'I didn\'t have a call'],
  },
  {
    id: 'received_emails_nb',
    question: 'Do you remember getting emails from us?',
    type: 'single',
    options: ['Yes', 'No', 'Not sure'],
  },
  {
    id: 'emails_feeling_nb',
    question: 'How did the emails feel?',
    subtitle: 'Select all that apply',
    type: 'multi',
    options: ['Informative', 'Useful', 'Too frequent', 'Annoying', 'Didn\'t read them'],
    showIf: 'received_emails_nb_yes',
  },
  {
    id: 'what_would_change',
    question: 'Is there anything we could have done differently to win your booking?',
    type: 'text',
    optional: true,
  },
  {
    id: 'still_consider',
    question: 'Would you still consider us for future events or recommend us to friends?',
    type: 'single',
    options: ['Yes', 'Maybe', 'No'],
  },
];

const PRICING_QUESTIONS = [
  {
    id: 'package_booked',
    question: 'Which package did you book?',
    type: 'text',
    bookedOnly: true,
  },
  {
    id: 'price_comparison',
    question: 'Compared to others, how did our price feel?',
    type: 'single',
    options: ['The most affordable', 'Cheaper than most', 'About the same', 'More expensive than most', 'The most expensive'],
  },
  {
    id: 'price_justified',
    question: 'If we were more expensive, what justified the price?',
    type: 'multi',
    options: ['Quality of work', 'Package inclusions', 'Style', 'Trust and confidence', 'Recommendations', 'Other'],
    optional: true,
    bookedOnly: true,
  },
  {
    id: 'price_vs_style',
    question: 'Did price influence your decision more than style?',
    type: 'single',
    options: ['Price was the main factor', 'Style was the main factor', 'Both equally', 'Neither — it was gut feeling'],
  },
  {
    id: 'wish_included',
    question: 'Was there anything you wished was included that wasn\'t?',
    type: 'text',
    optional: true,
  },
];

const FINAL_BOOKED = [
  {
    id: 'confidence',
    question: 'How confident are you that you made the right choice?',
    type: 'rating',
    labels: ['Not sure', '', 'Pretty confident', '', 'Absolutely certain'],
  },
  {
    id: 'recommend_before_film',
    question: 'Would you recommend us before even seeing your final film/photos?',
    type: 'single',
    options: ['Yes', 'Maybe', 'No'],
  },
  {
    id: 'what_would_you_say',
    question: 'If yes, what would you say to other couples?',
    type: 'text',
    optional: true,
  },
  {
    id: 'anything_else',
    question: 'Anything else you want to tell us?',
    type: 'text',
    optional: true,
  },
];

const FINAL_NOT_BOOKED = [
  {
    id: 'overall_experience',
    question: 'How would you rate your overall experience with us?',
    type: 'rating',
    labels: ['Poor', '', 'Okay', '', 'Excellent'],
  },
  {
    id: 'anything_else',
    question: 'Anything else you want to tell us?',
    type: 'text',
    optional: true,
  },
];

// --- Components ---

const SingleSelect = ({ options, value, onChange }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
    {options.map(opt => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(value === opt ? '' : opt)}
        className={`text-left px-4 py-3 rounded-xl text-sm transition-all border ${
          value === opt
            ? 'bg-gold/20 border-gold text-gold font-medium'
            : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30 hover:bg-white/10'
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

const MultiSelect = ({ options, value = [], onChange }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
    {options.map(opt => {
      const selected = value.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(selected ? value.filter(v => v !== opt) : [...value, opt])}
          className={`text-left px-4 py-3 rounded-xl text-sm transition-all border flex items-center gap-2 ${
            selected
              ? 'bg-gold/20 border-gold text-gold font-medium'
              : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30 hover:bg-white/10'
          }`}
        >
          <span className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center ${
            selected ? 'bg-gold border-gold' : 'border-white/30'
          }`}>
            {selected && <Check className="w-3 h-3 text-black" />}
          </span>
          {opt}
        </button>
      );
    })}
  </div>
);

const RatingSelect = ({ value, onChange, labels }) => (
  <div className="flex gap-2 justify-center">
    {[1,2,3,4,5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`w-14 h-14 rounded-xl text-lg font-bold transition-all border ${
          value === n
            ? 'bg-gold border-gold text-black'
            : 'bg-white/5 border-white/10 text-white/50 hover:border-white/30 hover:text-white'
        }`}
      >
        {n}
      </button>
    ))}
  </div>
);

const TextInput = ({ value, onChange, placeholder }) => (
  <textarea
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder || 'Type your answer...'}
    rows={3}
    className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all text-sm resize-none"
  />
);


const Feedback = () => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Step 0: details
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [service, setService] = useState('');
  const [booked, setBooked] = useState(''); // 'yes' or 'no'

  // All answers keyed by question id
  const [answers, setAnswers] = useState({});

  const setAnswer = (id, val) => setAnswers(prev => ({ ...prev, [id]: val }));

  const isBooked = booked === 'yes';

  // Dynamic sections based on booked status
  const getSections = () => {
    const decision = isBooked ? DECISION_BOOKED : DECISION_NOT_BOOKED;
    const pricing = PRICING_QUESTIONS.filter(q => !q.bookedOnly || isBooked);
    const final_ = isBooked ? FINAL_BOOKED : FINAL_NOT_BOOKED;
    return [FOUND_US_QUESTIONS, decision, pricing, final_];
  };

  const sections = getSections();

  const handleSubmit = async () => {
    setLoading(true);
    const allQuestions = sections.flat();
    const heard = answers['how_heard'] || [];
    const receivedEmails = answers['received_emails'] || '';
    const receivedEmailsNb = answers['received_emails_nb'] || '';

    const answerList = allQuestions
      .filter(q => {
        if (q.showIf === 'wedding_fair' && !heard.includes('Wedding fair')) return false;
        if (q.showIf === 'received_emails_yes' && receivedEmails !== 'Yes') return false;
        if (q.showIf === 'received_emails_nb_yes' && receivedEmailsNb !== 'Yes') return false;
        return true;
      })
      .map(q => ({
        question: q.question,
        answer: answers[q.id] || '',
      }));

    try {
      const res = await fetch(`${BACKEND_URL}/api/feedback-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, service, booked, answers: answerList }),
      });
      const data = await res.json();
      if (data.success) setSubmitted(true);
    } catch {
      // silent fail
    } finally {
      setLoading(false);
    }
  };

  const canAdvance = () => {
    if (step === 0) return name.trim() && email.trim() && service && booked;
    return true;
  };

  const isQuestionVisible = (q) => {
    if (q.showIf === 'wedding_fair') {
      const heard = answers['how_heard'] || [];
      return heard.includes('Wedding fair');
    }
    if (q.showIf === 'received_emails_yes') {
      return answers['received_emails'] === 'Yes';
    }
    if (q.showIf === 'received_emails_nb_yes') {
      return answers['received_emails_nb'] === 'Yes';
    }
    return true;
  };

  const renderQuestion = (q) => {
    if (!isQuestionVisible(q)) return null;

    const val = answers[q.id];
    return (
      <div key={q.id} className="mb-6">
        <p className="text-white text-sm font-medium mb-1">
          {q.question}
        </p>
        {q.subtitle && <p className="text-white/40 text-xs mb-2">{q.subtitle}</p>}
        {q.type === 'single' && <SingleSelect options={q.options} value={val || ''} onChange={v => setAnswer(q.id, v)} />}
        {q.type === 'multi' && <MultiSelect options={q.options} value={val || []} onChange={v => setAnswer(q.id, v)} />}
        {q.type === 'text' && <TextInput value={val} onChange={v => setAnswer(q.id, v)} />}
        {q.type === 'rating' && <RatingSelect value={val} onChange={v => setAnswer(q.id, v)} labels={q.labels} />}
      </div>
    );
  };

  const totalSteps = 5; // 0=details, 1-4=question sections
  const sectionTitles = isBooked
    ? ['', 'How you found us', 'The decision', 'Pricing & packages', 'Final thoughts']
    : ['', 'How you found us', 'Your experience', 'Pricing', 'Final thoughts'];
  const sectionHeadings = isBooked
    ? ['', 'Your journey to us', 'What made you choose us', 'Let\'s talk pricing', 'Nearly there!']
    : ['', 'Your journey to us', 'Help us understand', 'Let\'s talk pricing', 'Nearly there!'];

  if (submitted) {
    return (
      <div data-testid="feedback-page" className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="text-center max-w-md px-4 animate-fade-up">
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="font-serif text-3xl text-white mb-3">Thank you!</h2>
          <p className="text-white/60 text-sm">Your feedback means the world to us. It helps us keep doing what we love, even better.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="feedback-page" className="min-h-screen" style={{ backgroundColor: '#1A1A1A' }}>
      {/* Header */}
      <div className="border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-center">
          <img src="/wve-logo.png" alt="Wedding Video Essex" className="h-14" />
        </div>
      </div>

      {/* Progress */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4 max-w-2xl">
        <div className="flex items-center justify-between mb-2">
          {STEP_META.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <div key={i} className="flex flex-col items-center gap-1.5 flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isDone ? 'bg-gold text-black' : isActive ? 'bg-white/10 text-gold border border-gold' : 'bg-white/5 text-white/30'
                }`}>
                  {isDone ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`text-xs transition-colors ${isActive ? 'text-gold' : isDone ? 'text-white/60' : 'text-white/30'}`}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex gap-1 mt-3">
          {STEP_META.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-500 ${
              i <= step ? 'bg-gold' : 'bg-white/10'
            }`} />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-2xl">
        {/* Step 0: Details */}
        {step === 0 && (
          <div className="animate-fade-up">
            <div className="text-center mb-8">
              <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-3">Welcome</p>
              <h1 className="font-serif text-2xl sm:text-3xl text-white mb-2">We'd love your feedback</h1>
              <p className="text-white/50 text-sm">This helps us understand your experience and keep improving. Takes about 3 minutes.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 space-y-4">
              <div>
                <label className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1.5 block">Your name</label>
                <input
                  data-testid="fb-name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Emma & James"
                  className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3.5 text-white placeholder-white/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all"
                />
              </div>
              <div>
                <label className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1.5 block">Your email</label>
                <input
                  data-testid="fb-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3.5 text-white placeholder-white/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all"
                />
              </div>
              <div>
                <label className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1.5 block">What were you looking for?</label>
                <div className="grid grid-cols-3 gap-2">
                  {['Video', 'Photo', 'Both'].map(s => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`fb-service-${s.toLowerCase()}`}
                      onClick={() => setService(s)}
                      className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border ${
                        service === s ? 'bg-gold/20 border-gold text-gold' : 'bg-white/5 border-white/10 text-white/60 hover:border-white/30'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1.5 block">Did you book us?</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="fb-booked-yes"
                    onClick={() => setBooked('yes')}
                    className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border ${
                      booked === 'yes' ? 'bg-gold/20 border-gold text-gold' : 'bg-white/5 border-white/10 text-white/60 hover:border-white/30'
                    }`}
                  >
                    Yes — we booked you
                  </button>
                  <button
                    type="button"
                    data-testid="fb-booked-no"
                    onClick={() => setBooked('no')}
                    className={`px-4 py-3 rounded-xl text-sm font-medium transition-all border ${
                      booked === 'no' ? 'bg-gold/20 border-gold text-gold' : 'bg-white/5 border-white/10 text-white/60 hover:border-white/30'
                    }`}
                  >
                    No — we went elsewhere
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Question steps */}
        {step >= 1 && step <= 4 && (
          <div className="animate-fade-up">
            <div className="text-center mb-8">
              <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-3">
                {sectionTitles[step]}
              </p>
              <h1 className="font-serif text-2xl sm:text-3xl text-white mb-2">
                {sectionHeadings[step]}
              </h1>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              {sections[step - 1].map(renderQuestion)}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-3 mt-6">
          {step > 0 && (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-2 px-6 py-3.5 rounded-xl border border-white/20 text-white/60 hover:text-white hover:border-white/40 transition-all text-sm font-medium"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          )}
          <button
            type="button"
            data-testid="fb-next-btn"
            onClick={() => {
              if (step < 4) setStep(step + 1);
              else handleSubmit();
            }}
            disabled={!canAdvance() || loading}
            className="flex-1 bg-gold hover:bg-gold-light text-black font-semibold py-3.5 rounded-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Sending...
              </span>
            ) : step < 4 ? (
              <>Next <ChevronRight className="w-4 h-4" /></>
            ) : (
              'Submit Feedback'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
